import { supabase } from '@/lib/supabase'
import type {
  ElectionResult,
  ElectionResultWithDetails,
  NationalElectionSummary,
  PartyNationalResult,
  PartyResultComparison,
  CreateElectionResultInput,
} from '@/types'

// Query key factory for consistent cache management
export const electionResultKeys = {
  all: ['electionResults'] as const,
  byElection: (electionId: string) => ['electionResults', 'election', electionId] as const,
  byParty: (electionId: string, partyId: string) =>
    ['electionResults', 'election', electionId, 'party', partyId] as const,
  byDistrict: (electionId: string, districtId: string) =>
    ['electionResults', 'election', electionId, 'district', districtId] as const,
  byRegion: (electionId: string, regionId: string) =>
    ['electionResults', 'election', electionId, 'region', regionId] as const,
  national: (electionId: string) => ['electionResults', 'election', electionId, 'national'] as const,
  summary: (electionId: string) => ['electionResults', 'election', electionId, 'summary'] as const,
}

// ============================================================================
// Core Fetch Functions
// ============================================================================

/**
 * Fetches all results for an election with party, district, and region details
 * Orders by vote_count descending (highest votes first)
 */
export async function fetchElectionResults(
  electionId: string
): Promise<ElectionResultWithDetails[]> {
  const { data, error } = await supabase
    .from('election_results')
    .select(
      `
      *,
      party:parties(id, name, abbreviation, color),
      district:districts(id, name, region_id),
      region:regions(id, name)
    `
    )
    .eq('election_id', electionId)
    .order('vote_count', { ascending: false })

  if (error) {
    throw new Error(`Failed to fetch election results: ${error.message}`)
  }

  return data as ElectionResultWithDetails[]
}

/**
 * Fetches national-level results only (both district_id and region_id are NULL)
 * Orders by seats_won descending
 */
export async function fetchNationalResults(
  electionId: string
): Promise<ElectionResultWithDetails[]> {
  const { data, error } = await supabase
    .from('election_results')
    .select(
      `
      *,
      party:parties(id, name, abbreviation, color)
    `
    )
    .eq('election_id', electionId)
    .is('district_id', null)
    .is('region_id', null)
    .order('seats_won', { ascending: false })

  if (error) {
    throw new Error(`Failed to fetch national results: ${error.message}`)
  }

  return data as ElectionResultWithDetails[]
}

/**
 * Fetches results for a specific district
 * Orders by vote_count descending
 */
export async function fetchDistrictResults(
  electionId: string,
  districtId: string
): Promise<ElectionResultWithDetails[]> {
  const { data, error } = await supabase
    .from('election_results')
    .select(
      `
      *,
      party:parties(id, name, abbreviation, color)
    `
    )
    .eq('election_id', electionId)
    .eq('district_id', districtId)
    .order('vote_count', { ascending: false })

  if (error) {
    throw new Error(`Failed to fetch district results: ${error.message}`)
  }

  return data as ElectionResultWithDetails[]
}

/**
 * Fetches regional aggregate results (district_id is NULL, region_id is set)
 * Orders by seats_won descending
 */
export async function fetchRegionResults(
  electionId: string,
  regionId: string
): Promise<ElectionResultWithDetails[]> {
  const { data, error } = await supabase
    .from('election_results')
    .select(
      `
      *,
      party:parties(id, name, abbreviation, color)
    `
    )
    .eq('election_id', electionId)
    .eq('region_id', regionId)
    .is('district_id', null)
    .order('seats_won', { ascending: false })

  if (error) {
    throw new Error(`Failed to fetch region results: ${error.message}`)
  }

  return data as ElectionResultWithDetails[]
}

/**
 * Fetches all results for a specific party across all districts
 * Useful for showing a party's geographic performance
 * Orders by vote_share descending (best performing districts first)
 */
export async function fetchPartyResults(
  electionId: string,
  partyId: string
): Promise<ElectionResultWithDetails[]> {
  const { data, error } = await supabase
    .from('election_results')
    .select(
      `
      *,
      district:districts(id, name, region_id),
      region:regions(id, name)
    `
    )
    .eq('election_id', electionId)
    .eq('party_id', partyId)
    .order('vote_share', { ascending: false })

  if (error) {
    throw new Error(`Failed to fetch party results: ${error.message}`)
  }

  return data as ElectionResultWithDetails[]
}

/**
 * Fetches a single result record by ID
 * Returns null if not found
 */
export async function fetchElectionResultById(
  resultId: string
): Promise<ElectionResultWithDetails | null> {
  const { data, error } = await supabase
    .from('election_results')
    .select(
      `
      *,
      party:parties(id, name, abbreviation, color),
      district:districts(id, name, region_id),
      region:regions(id, name)
    `
    )
    .eq('id', resultId)
    .maybeSingle()

  if (error) {
    throw new Error(`Failed to fetch election result: ${error.message}`)
  }

  return data as ElectionResultWithDetails | null
}

// ============================================================================
// Aggregation Functions
// ============================================================================

/**
 * Fetches aggregated national summary for an election
 * Includes total votes, seats, turnout, and party-level breakdowns
 */
export async function fetchNationalSummary(
  electionId: string
): Promise<NationalElectionSummary> {
  const results = await fetchNationalResults(electionId)

  const totalVotes = results.reduce((sum, r) => sum + r.vote_count, 0)
  const totalSeats = results.reduce((sum, r) => sum + r.seats_won, 0)

  // Fetch election for turnout calculation
  const { data: election } = await supabase
    .from('elections')
    .select('results')
    .eq('id', electionId)
    .single()

  const turnoutPercentage =
    (election?.results as { turnout_percentage?: number } | null)?.turnout_percentage ?? 0

  return {
    election_id: electionId,
    total_votes: totalVotes,
    total_seats: totalSeats,
    turnout_percentage: turnoutPercentage,
    party_results: results.map((r): PartyNationalResult => ({
      party_id: r.party_id,
      party_name: r.party?.name ?? 'Unknown',
      party_abbreviation: r.party?.abbreviation ?? '??',
      party_color: r.party?.color ?? '#888888',
      total_votes: r.vote_count,
      vote_share: r.vote_share,
      seats_won: r.seats_won,
      seat_share: totalSeats > 0 ? (r.seats_won / totalSeats) * 100 : 0,
      vote_to_seat_ratio: r.vote_share > 0 ? r.seats_won / r.vote_share : 0,
    })),
  }
}

// ============================================================================
// Historical Comparison Functions
// ============================================================================

/**
 * Compares results between two elections
 * Returns comparison data for each party showing vote and seat changes
 */
export async function compareElections(
  currentElectionId: string,
  previousElectionId: string
): Promise<PartyResultComparison[]> {
  const [current, previous] = await Promise.all([
    fetchNationalResults(currentElectionId),
    fetchNationalResults(previousElectionId),
  ])

  const previousMap = new Map(previous.map((r) => [r.party_id, r]))

  return current.map((curr): PartyResultComparison => {
    const prev = previousMap.get(curr.party_id)
    return {
      party_id: curr.party_id,
      party_name: curr.party?.name ?? 'Unknown',
      party_color: curr.party?.color ?? '#888888',
      current_votes: curr.vote_count,
      current_share: curr.vote_share,
      current_seats: curr.seats_won,
      previous_votes: prev?.vote_count ?? 0,
      previous_share: prev?.vote_share ?? 0,
      previous_seats: prev?.seats_won ?? 0,
      vote_change: curr.vote_share - (prev?.vote_share ?? 0),
      seat_change: curr.seats_won - (prev?.seats_won ?? 0),
    }
  })
}

/**
 * Fetches all elections with results for a nation for historical analysis
 * Returns election IDs ordered by scheduled_tick descending (most recent first)
 */
export async function fetchElectionsWithResults(nationId: string): Promise<string[]> {
  const { data, error } = await supabase
    .from('elections')
    .select('id')
    .eq('nation_id', nationId)
    .eq('status', 'completed')
    .order('scheduled_tick', { ascending: false })

  if (error) {
    throw new Error(`Failed to fetch elections with results: ${error.message}`)
  }

  return data.map((e) => e.id)
}

// ============================================================================
// Write Functions (for election processing)
// ============================================================================

/**
 * Inserts a single election result record
 * Returns the created record
 */
export async function insertElectionResult(
  input: CreateElectionResultInput
): Promise<ElectionResult> {
  const { data, error } = await supabase
    .from('election_results')
    .insert({
      election_id: input.election_id,
      party_id: input.party_id,
      district_id: input.district_id ?? null,
      region_id: input.region_id ?? null,
      vote_count: input.vote_count,
      vote_share: input.vote_share,
      seats_won: input.seats_won ?? 0,
      turnout_count: input.turnout_count ?? null,
      eligible_voters: input.eligible_voters ?? null,
    })
    .select()
    .single()

  if (error) {
    throw new Error(`Failed to insert election result: ${error.message}`)
  }

  return data as ElectionResult
}

/**
 * Inserts multiple election results in batch
 * Used for bulk insertion during election processing
 */
export async function insertElectionResults(
  results: CreateElectionResultInput[]
): Promise<void> {
  if (results.length === 0) return

  const records = results.map((input) => ({
    election_id: input.election_id,
    party_id: input.party_id,
    district_id: input.district_id ?? null,
    region_id: input.region_id ?? null,
    vote_count: input.vote_count,
    vote_share: input.vote_share,
    seats_won: input.seats_won ?? 0,
    turnout_count: input.turnout_count ?? null,
    eligible_voters: input.eligible_voters ?? null,
  }))

  const { error } = await supabase.from('election_results').insert(records)

  if (error) {
    throw new Error(`Failed to batch insert election results: ${error.message}`)
  }
}

/**
 * Upserts an election result (insert or update on conflict)
 * Useful for recalculations or corrections
 */
export async function upsertElectionResult(
  input: CreateElectionResultInput
): Promise<ElectionResult> {
  const { data, error } = await supabase
    .from('election_results')
    .upsert(
      {
        election_id: input.election_id,
        party_id: input.party_id,
        district_id: input.district_id ?? null,
        region_id: input.region_id ?? null,
        vote_count: input.vote_count,
        vote_share: input.vote_share,
        seats_won: input.seats_won ?? 0,
        turnout_count: input.turnout_count ?? null,
        eligible_voters: input.eligible_voters ?? null,
      },
      {
        onConflict: 'election_id,party_id,district_id',
      }
    )
    .select()
    .single()

  if (error) {
    throw new Error(`Failed to upsert election result: ${error.message}`)
  }

  return data as ElectionResult
}

/**
 * Updates an existing election result
 * Returns the updated record
 */
export async function updateElectionResult(
  resultId: string,
  updates: Partial<Omit<CreateElectionResultInput, 'election_id' | 'party_id'>>
): Promise<ElectionResult> {
  const { data, error } = await supabase
    .from('election_results')
    .update(updates)
    .eq('id', resultId)
    .select()
    .single()

  if (error) {
    throw new Error(`Failed to update election result: ${error.message}`)
  }

  return data as ElectionResult
}

/**
 * Deletes all results for an election
 * Useful for re-processing or resetting election results
 */
export async function deleteElectionResults(electionId: string): Promise<void> {
  const { error } = await supabase
    .from('election_results')
    .delete()
    .eq('election_id', electionId)

  if (error) {
    throw new Error(`Failed to delete election results: ${error.message}`)
  }
}

// ============================================================================
// Query Helpers
// ============================================================================

/**
 * Checks if results exist for an election
 */
export async function hasElectionResults(electionId: string): Promise<boolean> {
  const { count, error } = await supabase
    .from('election_results')
    .select('*', { count: 'exact', head: true })
    .eq('election_id', electionId)

  if (error) {
    throw new Error(`Failed to check election results: ${error.message}`)
  }

  return (count ?? 0) > 0
}

/**
 * Gets the count of result records for an election
 */
export async function getElectionResultsCount(electionId: string): Promise<number> {
  const { count, error } = await supabase
    .from('election_results')
    .select('*', { count: 'exact', head: true })
    .eq('election_id', electionId)

  if (error) {
    throw new Error(`Failed to get election results count: ${error.message}`)
  }

  return count ?? 0
}

/**
 * Gets the winning party for an election (most seats)
 * Returns null if no results or tie
 */
export async function getElectionWinner(
  electionId: string
): Promise<ElectionResultWithDetails | null> {
  const results = await fetchNationalResults(electionId)

  if (results.length === 0) return null

  // Sort by seats_won descending
  const sorted = [...results].sort((a, b) => b.seats_won - a.seats_won)

  // Check for tie
  if (sorted.length > 1 && sorted[0].seats_won === sorted[1].seats_won) {
    return null // Tie - no clear winner
  }

  return sorted[0]
}
